import type { TodoListItemButtonVariant } from '@ksyos/design-system/dist/components/todo-list/todo-list.types';
import type { JSX } from 'react';
import { Text, TodoList, TodoListItem, TodoListSection } from '@ksyos/design-system';
import { orderBy, partition, uniqBy } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { ProcessTask } from '@common/interfaces/api/processTasksApi';
import type { IReduxState } from '@frontend/configureStore';
import { getTasks } from '@frontend/services/api/processesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDeepCompareEffect } from '@frontend/utils/hooks/useDeepCompareEffect';
import type { ProcessActionButtonItem } from './processActionButtonItem';

type ProcessTodoListProps = Readonly<{
    isRenderedInSplitView?: boolean;
    items: ProcessActionButtonItem[];
    processId: string;
}>;

const dangerItemLabels = ['Afbreken voortraject'];

export function ProcessTodoList({ items, processId, isRenderedInSplitView }: ProcessTodoListProps): JSX.Element {
    const abortSignal = useAbortSignal();
    const { organizationId } = useSelector((state: IReduxState) => state.authentication);
    const [tasks, setTasks] = useState<ProcessActionButtonItem[]>([]);
    const actions = determineActions(items);

    useDeepCompareEffect(() => {
        getTasks(processId, ['inProgress', 'open'], abortSignal).then((taskList) => {
            const allTasks = determineTasks(items, taskList, organizationId);
            const orderedTasks = orderTasks(allTasks);
            setTasks(orderedTasks);
        });
    }, [items, processId]);

    return (
        <TodoList isRenderedInSplitView={isRenderedInSplitView}>
            {tasks.length + actions.length === 0 ? null : (
                <>
                    <TodoListSection
                        icon="todo"
                        isExpanded={true}
                        title="Taken"
                    >
                        <TodoListItems
                            buttonVariant="solid"
                            emptyText="Er zijn op dit moment geen openstaande taken"
                            items={tasks}
                        />
                    </TodoListSection>
                    <TodoListSection
                        icon="jobs"
                        isExpanded={false}
                        title="Acties"
                    >
                        <TodoListItems
                            buttonVariant="outline"
                            emptyText="Je hebt op dit moment geen acties"
                            items={actions}
                        />
                    </TodoListSection>
                </>
            )}
        </TodoList>
    );
}

function TodoListItems({
    buttonVariant,
    emptyText,
    items,
}: Readonly<{
    buttonVariant: TodoListItemButtonVariant;
    emptyText: string;
    items: ProcessActionButtonItem[];
}>): JSX.Element {
    return (
        <>
            {items.length > 0 ? (
                items.map(({ badge, isDangerous, isPaused, key, onClick, organizationName, label }) => (
                    <TodoListItem
                        badge={badge}
                        buttonVariant={buttonVariant}
                        color={isDangerous ? 'danger' : 'primary'}
                        data-automation-test={label}
                        disabledMessage={
                            onClick
                                ? 'Het proces van de patiënt is gepauzeerd, hierdoor is het aantal taken en/of acties beperkt'
                                : undefined
                        }
                        icon={isDangerous ? 'close' : 'caret-right'}
                        isDisabled={isPaused}
                        key={key}
                        onClick={onClick}
                        subtitle={organizationName}
                        title={label}
                    />
                ))
            ) : (
                <Text
                    marginBottom={4}
                    marginLeft={1}
                    size="sm"
                >
                    {emptyText}
                </Text>
            )}
        </>
    );
}

/**
 * Determine the tasks to show in the TodoList
 *
 * @param items a list of known ProcessActionButtonItems
 * @param tasks a list of tasks that might overlap with ProcessActionButtonItems
 * @param userOrganisationId the id of the user's organisation
 *
 * @returns a list of tasks shaped as ProcessActionButtonItem
 */
function determineTasks(
    items: ProcessActionButtonItem[],
    tasks: ProcessTask[],
    userOrganisationId: string
): ProcessActionButtonItem[] {
    const actionTasks = items.filter((item) => item.processTaskCreatedDatetime !== undefined);
    const uniqueTasks = uniqBy(tasks, (task) => task.processWorkflowStepId ?? task.serviceWorkflowStep.id);
    const result: ProcessActionButtonItem[] = [];

    for (const task of uniqueTasks) {
        const existingItem = actionTasks.find((actionTask) =>
            actionTask.processWorkflowStepId
                ? actionTask.processWorkflowStepId === task.processWorkflowStepId
                : actionTask.serviceWorkflowStepId === task.serviceWorkflowStepId
        );
        if (existingItem) {
            result.push({
                ...existingItem,
                isDangerous: dangerItemLabels.includes(existingItem.label),
                organizationName: task.performerOrganization?.name,
            });
        } else {
            result.push({
                badge: task.performerOrganization?.id === userOrganisationId ? 'Bij collega' : undefined,
                key: task.processWorkflowStepId ?? task.serviceWorkflowStep.id,
                label: task.serviceWorkflowStep.label ?? task.serviceWorkflowStep.name,
                organizationName: task.performerOrganization?.name,
                processTaskCreatedDatetime: task.createdDatetime,
                serviceWorkflowStepId: task.serviceWorkflowStepId,
            });
        }
    }

    return result;
}

/**
 * Orders tasks based on the following criteria:
 * - First tasks in Draft, then User tasks, then Paused tasks (draft first), then Colleague tasks and lastly Other tasks
 * - Within each group, order by processTaskCreatedDatetime descending and label ascending
 * @param tasks a list of ProcessActionButtonItem
 *
 * @returns a list of ordered ProcessActionButtonItem
 */
function orderTasks(tasks: ProcessActionButtonItem[]): ProcessActionButtonItem[] {
    const sortedTasks = orderBy(tasks, ['processTaskCreatedDatetime', 'label'], ['desc', 'asc']);

    const [userTasks, nonUserTasks] = partition(sortedTasks, (item) => item.onClick !== undefined);
    const [inProgressTasks, openTasks] = partition(userTasks, (item) => item.badge === 'Concept');
    const [inProgressTasksPaused, inProgressTasksActive] = partition(inProgressTasks, (item) => item.isPaused);
    const [openTasksPaused, openTasksActive] = partition(openTasks, (item) => item.isPaused);
    const [colleagueTasks, otherTasks] = partition(nonUserTasks, (item) => item.badge === 'Bij collega');

    return [
        ...inProgressTasksActive,
        ...openTasksActive,
        ...inProgressTasksPaused,
        ...openTasksPaused,
        ...colleagueTasks,
        ...otherTasks,
    ];
}

function determineActions(items: ProcessActionButtonItem[]): ProcessActionButtonItem[] {
    const actions = items
        .filter((item) => item.processTaskCreatedDatetime === undefined)
        .map((item) => {
            item.isDangerous = dangerItemLabels.includes(item.label);

            return item;
        });

    return orderBy(actions, ['isDangerous', 'label']);
}
